import { useEffect, useMemo, useRef, useState } from "react";

const useIsHoverFocused = (...refs) => {
  const isHovered = useIsHovered(...refs);
  const isFocused = useIsfocused(...refs);

  const isHoverFocused = useMemo(
    () => isHovered || isFocused,
    [isFocused, isHovered]
  );

  return isHoverFocused;
};

// Pointer events and not mouseover/mouseout, which carry no pointerType: a tap
// sends a real mouseover before the click, and anything that reacts to it by
// changing what's on screen makes iOS withhold mousedown/click until a second
// tap. CustomScrollbar hovers its whole scroll container, so that cost every
// button on the page its first tap. A finger has no hover -- drop it here and
// every consumer gets the right answer.
//
// addEventListener and not the onpointerover property the focus hooks below
// still use: jsdom implements onmouseover but not onpointerover, so the
// property form is untestable. It also stops two hooks sharing one ref from
// silently stealing each other's handler, which the old "set it once" guard
// did.
const useIsHovered = (...refs) => {
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    const elements = refs.map((ref) => ref?.current).filter(Boolean);

    const handleOver = (e) => {
      if (e.pointerType === "touch") return;
      setIsHovered(e.currentTarget);
    };
    const handleOut = (e) => {
      if (e.pointerType === "touch") return;
      setIsHovered(false);
    };

    elements.forEach((el) => {
      el.addEventListener("pointerover", handleOver);
      el.addEventListener("pointerout", handleOut);
    });

    return () => {
      elements.forEach((el) => {
        el.removeEventListener("pointerover", handleOver);
        el.removeEventListener("pointerout", handleOut);
      });
    };
  }, [refs]);

  return isHovered;
};

const useIsfocused = (...refs) => {
  const [isFocused, setIsFocused] = useState(false);
  useEffect(() => {
    refs.forEach((ref) => {
      const current = ref?.current;
      if (!current) return;
      if (!current.onfocus)
        current.onfocus = () => {
          setIsFocused(current);
          return;
        };
      if (!current.onblur) current.onblur = () => setIsFocused(false);
      if (document.activeElement === current) {
        setIsFocused(current);
        return;
      }
    });
  }, [refs]);

  return isFocused;
};

const useIsFocusVisible = (...refs) => {
  const [isFocusVisible, setIsFocusVisible] = useState(false);

  // The rest parameter is a new array every render, so it can never be a
  // dependency. Attach against the elements instead and only touch them when
  // those change — re-running this per render is how a group row ended up
  // carrying 60 listeners after ten hovers.
  const refsRef = useRef(refs);
  refsRef.current = refs;
  const attachedRef = useRef([]);

  const handlersRef = useRef();
  if (!handlersRef.current)
    handlersRef.current = {
      // currentTarget rather than a closed-over element, so one set of handlers
      // covers every ref and stays stable for removeEventListener.
      keyup: (e) => {
        if (document.activeElement === e.currentTarget)
          setIsFocusVisible(e.currentTarget);
      },
      mousedown: () => setIsFocusVisible(false),
      blur: () => setIsFocusVisible(false),
    };

  useEffect(() => {
    const handlers = handlersRef.current;
    const els = refsRef.current.map((ref) => ref?.current).filter(Boolean);
    const attached = attachedRef.current;

    if (
      els.length === attached.length &&
      els.every((el, i) => el === attached[i])
    )
      return;

    attached.forEach((el) => {
      Object.entries(handlers).forEach(([type, fn]) =>
        el.removeEventListener(type, fn)
      );
    });
    els.forEach((el) => {
      Object.entries(handlers).forEach(([type, fn]) =>
        el.addEventListener(type, fn)
      );
    });
    attachedRef.current = els;
  });

  useEffect(
    () => () => {
      const handlers = handlersRef.current;
      attachedRef.current.forEach((el) => {
        Object.entries(handlers).forEach(([type, fn]) =>
          el.removeEventListener(type, fn)
        );
      });
      attachedRef.current = [];
    },
    []
  );

  return isFocusVisible;
};

export default useIsHoverFocused;
export { useIsHovered, useIsfocused, useIsFocusVisible };
